import { Alert } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import CFactsheetDisclaimer from '../../../components/CFactsheetDisclaimer';
import CLocalizedText from '../../../components/CLocalizedText';
import CModal from '../../../components/CModal';
import CPageModule from '../../../components/CPageModule';
import ImageWrapper from '../../../components/ImageWrapper';
import NotFoundModule from '../../../components/PageGates/NotFoundModule';
import Page from '../../../components/layout/Page';
import { EStylings } from '../../../enums/EStylings';
import { PageRoutes } from '../../../enums/enums';
import { updateStateAttribute } from '../../../helpers/helper';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { useUsageTrackingInfo } from '../../../hooks/useUsageTrackingInfo';
import type { GhostPostArticleModel } from '../../../models/GhostPostArticleModel';
import type { IRequestState } from '../../../models/IRequestState';
import type { IUsageTrackingInfo } from '../../../models/usage-tracking/IUsageTracking';
import { SvNewsService } from '../../../services/SvNewsService';
import { SvUsageTrackingServices } from '../../../services/SvUsageTrackingServices';
import { addBreadcrumbTextReplacement } from '../../../store/appSlice';
import PFactsheetTerms from '../../publicFactsheet/PFactsheetTerms';

interface State {
  post?: GhostPostArticleModel;
  hideFeatureImage?: boolean;
  disclaimer?: string;
  reqStatus: IRequestState;
  termsShown: boolean;
}

export default function ArticlesEntry() {
  const location = useLocation();
  const navigate = useNavigate();
  const slug = location.pathname.split('/').slice(-1).pop() ?? '';
  const dispatch = useAppDispatch();
  const { locale } = useAppSelector((state) => state.dashboard);
  const [state, setState] = useState<State>({
    reqStatus: { isLoading: true, isError: false },
    termsShown: false
  });

  const usageTrackingParams: IUsageTrackingInfo = useUsageTrackingInfo(`news-blog-${slug}`);

  const updateState = (o: Partial<State>) => {
    setState((state) => ({ ...state, ...o }));
  };
  const { post, reqStatus } = state;

  const loadPost = useCallback(
    async (slug: string) => {
      updateState({ reqStatus: { ...reqStatus, isLoading: true } });

      const resp = await SvNewsService.getPost({ slug });
      const ok = await resp.isSuccess();

      if (!ok) {
        updateState({
          reqStatus: { isLoading: false, isError: true }
        });
        return;
      }

      const post = await resp.getData();

      updateState({
        post,
        reqStatus: { ...reqStatus, isLoading: false }
      });

      updateStateAttribute<State>({ termsShown: !post?.disclaimerWall }, setState);

      dispatch(addBreadcrumbTextReplacement([slug, post?.title]));
    },
    [dispatch, location.pathname]
  );

  const computeGalleryImageHeight = () => {
    setTimeout(() => {
      const galleryRows = document.querySelectorAll<HTMLElement>('.kg-gallery-row');
      for (const gal of galleryRows) {
        let minHInRow = 0;
        let maxHInRow = window.innerHeight * 0.4;
        for (const imageContainer of gal.childNodes) {
          for (const imageTag of imageContainer.childNodes) {
            const imageH = (imageTag as any).height as number;
            maxHInRow = maxHInRow > imageH ? imageH : maxHInRow;
            minHInRow = minHInRow < imageH ? imageH : minHInRow;
          }
        }

        for (const imageContainer of gal.childNodes) {
          for (const imageTag of imageContainer.childNodes) {
            (imageTag as HTMLElement).style.height = `${Math.round(0.4 * (maxHInRow + minHInRow))}px`;
          }
        }
      }

      const galleries = document.querySelectorAll<HTMLElement>('.kg-gallery-card');
      for (const gallery of galleries) {
        gallery.style.display = 'block';
      }
    }, 1);
  };

  const doUserAcceptsTerms = async (termsAccepted: boolean) => {
    if (termsAccepted) {
      updateStateAttribute<State>({ termsShown: true }, setState);
    } else {
      navigate('..');
    }
  };

  useEffect(() => {
    (async () => {
      await loadPost(slug);
      computeGalleryImageHeight();
    })();
  }, [loadPost]);

  useEffect(() => {
    (async () => {
      if (state.termsShown) {
        await SvUsageTrackingServices.createUsageTrackingRecord(usageTrackingParams);
      }
    })();
  }, [state.termsShown]);

  return (
    <>
      {state.reqStatus.isError && !state.reqStatus.isLoading && <NotFoundModule />}
      {state.post?.disclaimerWall && (
        <CModal
          onClose={() => {
            doUserAcceptsTerms(false);
          }}
          open={!state.termsShown}
        >
          <PFactsheetTerms
            onAccept={() => {
              doUserAcceptsTerms(true);
            }}
          />
        </CModal>
      )}
      {(!state.reqStatus.isError || state.reqStatus.isLoading) && (
        <Page>
          <CPageModule
            apiRequestState={reqStatus}
            errorMessage={<Alert severity={'error'}>The requested article could not be found</Alert>}
          >
            {post && (
              <>
                <div className={'flex gap-2'}>
                  {post?.tags?.map((e, ix) => {
                    return (
                      <span key={e.slug}>
                        <Link
                          to={`../../../${PageRoutes.DASHBOARD_OUTLET}/${PageRoutes.NEWS_OUTLET}/${PageRoutes.NEWS_ARTICLES_OUTLET}?tag=${e.slug}`}
                        >
                          <span className={'uppercase font-bold'}>{e.name}</span>
                          {ix + 1 !== (post?.tags?.length ?? 0) && <span>, </span>}
                        </Link>
                      </span>
                    );
                  })}
                </div>
                <h1 className={'text-5xl'}>{post.title}</h1>
                <div>
                  <p className={'font-bold'}>{post.authors.map((x) => x.name).join(', ')}</p>
                  <p>
                    {new Date(post.createdAt).toLocaleString(locale, {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      timeZoneName: 'short'
                    })}{' '}
                    {!!post.readingTime && (
                      <>
                        &#8729; {post.readingTime}&nbsp;
                        <CLocalizedText dictKey={'newsMinsReadingTime'} inline />
                      </>
                    )}
                  </p>
                </div>
              </>
            )}
          </CPageModule>
          {post?.featureImage && !state.hideFeatureImage && (
            <CPageModule fullWidth style={{ paddingTop: 0 }}>
              <figure className={'overflow-hidden m-auto w-full'} style={{ maxWidth: EStylings.MAX_PAGE_MODULE_WIDTH }}>
                <ImageWrapper
                  src={post.featureImage}
                  alt={post?.featureImageCaption}
                  className={'h-full w-full object-cover text-xl'}
                  onFail={() => {
                    updateStateAttribute<State>({ hideFeatureImage: true }, setState);
                  }}
                />
                {post.featureImageCaption && (
                  <figcaption dangerouslySetInnerHTML={{ __html: post?.featureImageCaption }} />
                )}
              </figure>
            </CPageModule>
          )}
          {post && (
            <CPageModule style={{ paddingTop: 0 }}>
              <div className={'post-content'} dangerouslySetInnerHTML={{ __html: post?.html }} />
            </CPageModule>
          )}
          {/* {post && ( */}
          {/*   <PageModule background={<div className={'bg-slate-50'} />}> */}
          {/*     <p className={'uppercase text-sm font-bold'}>About the author{post.authors.length > 1 ? 's' : ''}</p> */}
          {/*     <div className={'flex flex-col gap-4'}> */}
          {/*       {post.authors?.map((x) => ( */}
          {/*         <div className={'flex gap-5'} key={x.id}> */}
          {/*           <ImageWrapper */}
          {/*             src={x.profile_image} */}
          {/*             alt={x.name} */}
          {/*             hideOnFail */}
          {/*             className={'object-cover aspect-square w-20 h-20 border rounded-full overflow-hidden flex-none flex'} */}
          {/*           /> */}
          {/*           <div className={'flex flex-col gap-2 flex-grow justify-center'}> */}
          {/*             <h5 className={'text-3xl text-teal-1'}>{x.name}</h5> */}
          {/*             <p>{x.bio}</p> */}
          {/*           </div> */}
          {/*         </div> */}
          {/*       ))} */}
          {/*     </div> */}
          {/*   </PageModule> */}
          {/* )} */}
          {post && <CFactsheetDisclaimer />}
        </Page>
      )}
    </>
  );
}
